"use client";

import {
  identifyUser,
  sendSessionPropertyEvent,
} from "@dynatrace/rum-javascript-sdk/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { getCurrentJwt } from "#app/(unauthorized)/authentication/jwt";
import {
  logoutOfIc,
  useSessionBroadcastChannelSendMessage,
} from "#app/(unauthorized)/authentication/jwt-hooks";
import { cacheJwt } from "#app/(unauthorized)/authentication/jwtStorageService";
import { allQueryKeys } from "#app/_api/allQueryKeys";
import { postAuthVendorAuthorize } from "#app/_api/bol-authentication-service/post-authorize";
import { cacheWidgetMfaSession } from "#app/_mfa/widget-helpers";
import AuthVendorWidget, {
  AUTH_VENDOR_WIDGET_REFRESH_INTERVAL_MS,
} from "#app/_ui/components/authvendor/AuthVendorWidget";
import { ErrorCard } from "#app/_ui/components/cards/ErrorCard";
import { PendingCard } from "#app/_ui/components/cards/PendingCard";
import { usePageMessage } from "#app/_ui/components/errors/use-page-message";
import { useOutageBypassPersistedCode } from "#app/_ui/components/outages/use-outage-bypass";
import { logError } from "#app/lib/logger";
import { getRedirectUrl } from "#app/lib/searchParamsUtil";
import { Logo } from "#ui/logo";
import { LogoDiscoverTheBairdDifference } from "#ui/logo-discover-the-baird-difference";
import { cx } from "#ui/style.utils";
import { H1 } from "#ui/typography";

import { SignInAlertCallout } from "../SignInAlerts";
import { reduceSignInAlerts } from "../SignInAlerts.utils";

export function SignInForm({
  className,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"div">, "children">) {
  // Server data hooks
  const outageBypasscode = useOutageBypassPersistedCode();
  const dataPageMessage = usePageMessage({ path: "/sign-in/institutional" });

  // State hooks
  const [statusCode, setStatusCode] = useState(0);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [widgetComplete, setWidgetComplete] = useState(false);

  // Utils hooks
  const router = useRouter();
  const searchParams = useSearchParams();
  const { sendMessage } = useSessionBroadcastChannelSendMessage();

  // Form submission hooks
  const queryClient = useQueryClient();
  const {
    data: authVendorWidgetToken,
    isSuccess: isSuccessDaviniciToken,
    isError: isErrorDaviniciToken,
    isPending: isPendingDaviniciToken,
    refetch: refetchAuthVendorWidgetToken,
  } = useQuery({
    ...allQueryKeys.bolAuthenticationService.authVendorWidgetToken({
      sessionType: "WebInstitutionalSignIn",
    }),
    refetchInterval: AUTH_VENDOR_WIDGET_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  //AuthVendor login
  const { mutateAsync: authVendorAuthorize } = useMutation({
    mutationFn: postAuthVendorAuthorize,
    onSuccess: async (data) => {
      if (data.mfa !== null) {
        cacheWidgetMfaSession(data.mfa);
      }
      cacheJwt(data.jwt);
      const currJwt = getCurrentJwt(true);
      await queryClient.cancelQueries();
      await queryClient.resetQueries();

      setStatusCode(200);
      setStatusMessage(null);

      try {
        logoutOfIc();
        const sessionProperties = {
          seg: currJwt.segment.toString(),
          obosub: currJwt.obosubject,
        } as const;

        const sessionPropertiesGrail = {
          "session_properties.seg": currJwt.segment.toString(),
          "session_properties.obosub": currJwt.obosubject,
        } as const;

        window?.dtrum?.identifyUser?.(currJwt.subject);
        window?.dtrum?.sendSessionProperties?.(
          undefined,
          undefined,
          sessionProperties,
          undefined,
        );
        identifyUser(currJwt.subject);
        sendSessionPropertyEvent(sessionPropertiesGrail);
        // Don't expose any errors from RUM
        // oxlint-disable-next-line no-empty
      } catch (_) {}

      const searchParamsObj = Object.fromEntries(searchParams.entries());
      const redirectUrl = getRedirectUrl(searchParamsObj, currJwt.segment);
      sendMessage({ type: "SIGN_IN", payload: currJwt.subject });

      return router.replace(redirectUrl);
    },
  });

  const callAuthVendorAuthorize = useCallback(
    async (data: string) => {
      try {
        await authVendorAuthorize({
          AuthVendorJwt: data,
          outageBypasscode: outageBypasscode ?? undefined,
        });
      } catch (err: unknown) {
        logError(err);
        if (err instanceof HTTPError) {
          if (err.response.status === 403) {
            const statusMessage = await err.response.json<string | null>();
            setStatusCode(err.response.status);
            setStatusMessage(statusMessage);
          } else {
            setStatusCode(err.response.status);
            setStatusMessage(null);
          }
          return;
        }
        setStatusCode(500);
      }
    },
    [authVendorAuthorize, outageBypasscode],
  );

  function widgetSuccess(event: any) {
    setWidgetComplete(true);
    void callAuthVendorAuthorize(event.access_token);
  }

  const widgetError = useCallback(
    (event: any) => {
      if (event.message.toLowerCase() == "session expired or not found") {
        void queryClient.invalidateQueries({
          queryKey: allQueryKeys.bolAuthenticationService.authVendorWidgetToken(
            {
              sessionType: "WebInstitutionalSignIn",
            },
          ).queryKey,
        });
      }
    },
    [queryClient],
  );

  useEffect(() => {
    function handleCancelMessage(event: MessageEvent) {
      try {
        // Check if event origin matches current window origin (case insensitive)
        if (
          window.location.origin.toLowerCase() !== event.origin?.toLowerCase()
        ) {
          return;
        }

        const messageData =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        // Check if this is the MFA cancel action
        if (
          messageData.Type === "Router" &&
          messageData.Detail === "MfaCancelAction"
        ) {
          // TODO: Replace with custom modal to match BOL styling and provide better UX around cancellation
          // oxlint-disable-next-line no-alert
          const confirmed = window.confirm(
            "Are you sure you want to cancel institutional sign in?",
          );

          if (confirmed) {
            router.push("/sign-in/institutional");
          }
        }
      } catch (error) {
        // Ignore parsing errors for non-JSON messages
        console.error(
          "Message parsing error (expected for some messages):",
          error,
        );
      }
    }

    window.addEventListener("message", handleCancelMessage);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("message", handleCancelMessage);
    };
  }, [router]);

  // Alerts
  const {
    alertPrimaryCode,
    alertPrimaryMessage,
    alertSecondaryCode,
    alertSecondaryMessage,
    isPageUnavailable,
  } = reduceSignInAlerts({
    dataPageMessage,
    searchParams,
    statusCode,
    statusMessage,
  });

  return (
    <div {...props} className={cx("flex flex-col gap-50px", className)}>
      {isPageUnavailable ? null : (
        <div className="-mt-40px grid gap-10px md:-mt-30px">
          <SignInAlertCallout
            code={alertPrimaryCode}
            message={alertPrimaryMessage}
          />
          <SignInAlertCallout
            code={alertSecondaryCode}
            message={alertSecondaryMessage}
          />
        </div>
      )}

      <div
        className={cx(
          "mx-auto flex h-full max-w-280px flex-col justify-between gap-40px",
          "[@media((width_>=_1280px)_and_(height_>=_1024px))]:gap-120px",
        )}
      >
        <Logo
          size="md"
          layout="mark-and-word"
          className="mx-auto mb-15px max-md:w-240px md:mb-34px md:mt-20px xl:mb-0 xl:mt-0"
        />

        {isPageUnavailable ? (
          <div className="flex flex-col gap-30px">
            <H1 size="h2" className="mb-10px text-center">
              Institutional Sign In
            </H1>
            <SignInAlertCallout
              code={alertPrimaryCode}
              message={alertPrimaryMessage}
            />
          </div>
        ) : isPendingDaviniciToken || widgetComplete ? (
          <PendingCard />
        ) : isErrorDaviniciToken ? (
          <ErrorCard />
        ) : isSuccessDaviniciToken ? (
          <AuthVendorWidget
            accessToken={authVendorWidgetToken.accessToken}
            companyId={authVendorWidgetToken.companyId}
            policyId={authVendorWidgetToken.policyId}
            widgetContainerSelector="skWidget"
            extraParameters=""
            onSuccess={widgetSuccess}
            onError={widgetError}
            onWidgetReactivated={refetchAuthVendorWidgetToken}
            widgetRefreshIntervalMs={AUTH_VENDOR_WIDGET_REFRESH_INTERVAL_MS}
          />
        ) : (
          <ErrorCard />
        )}

        <LogoDiscoverTheBairdDifference
          aria-hidden
          className="mx-auto max-w-187px max-xl:hidden"
        />
      </div>
    </div>
  );
}
